import React, { useState, useEffect, useRef } from 'react';
import './css/ManicMiner.css';

const ManicMiner = () => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('playing'); // playing, won, dead
  const [score, setScore] = useState(0);
  const [air, setAir] = useState(100);
  const [lives, setLives] = useState(3);

  const airRef = useRef(100);
  const scoreRef = useRef(0);
  const gameStateRef = useRef('playing');

  const gameRef = useRef({
    player: {
      x: 30,
      y: 368,
      width: 16,
      height: 16,
      velocityY: 0,
      velocityX: 0,
      isJumping: false,
      onGround: false,
      direction: 1, // 1 = right, -1 = left
      animFrame: 0
    },
    keys: {
      left: false,
      right: false,
      jump: false
    },
    platforms: [
      // Central Cavern authentic layout
      { x: 0, y: 392, width: 512, height: 24 }, // Main floor
      { x: 0, y: 344, width: 72, height: 16 },
      { x: 112, y: 296, width: 80, height: 16 },
      { x: 240, y: 248, width: 80, height: 16 },
      { x: 360, y: 200, width: 88, height: 16 },
      { x: 248, y: 152, width: 96, height: 16 },
      { x: 96, y: 200, width: 88, height: 16 },
      { x: 416, y: 120, width: 96, height: 16 },
      { x: 0, y: 80, width: 144, height: 16 },
    ],
    enemies: [
      { x: 130, y: 280, direction: 1, speed: 0.2, minX: 112, maxX: 180 },
      { x: 260, y: 232, direction: 1, speed: 0.2, minX: 240, maxX: 310 }, 
      { x: 380, y: 184, direction: 1, speed: 0.2, minX: 360, maxX: 435 },
      { x: 110, y: 184, direction: 1, speed: 0.2, minX: 96, maxX: 170 },
      { x: 440, y: 104, direction: 1, speed: 0.2, minX: 416, maxX: 500 },
    ],
    items: [
      { x: 40, y: 328, collected: false },
      { x: 140, y: 328, collected: false },
      { x: 265, y: 328, collected: false },
      { x: 390, y: 328, collected: false },
      { x: 280, y: 328, collected: false },
      { x: 120, y: 328, collected: false },
      { x: 460, y: 328, collected: false },
      { x: 60, y: 328, collected: false },
    ],
    exit: { x: 480, y: 362, width: 24, height: 30, active: false },
    itemsCollected: 0,
    totalItems: 8
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const game = gameRef.current;
    let animationFrameId;
    let lastTime = 0;
    let airTimer = 0;

    // Keyboard handlers
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') game.keys.left = true;
      if (e.key === 'ArrowRight') game.keys.right = true;
      if (e.key === ' ' || e.key === 'ArrowUp') {
        e.preventDefault();
        game.keys.jump = true;
      }
      if (e.key === 'r' && gameStateRef.current !== 'playing') {
        resetGame();
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'ArrowLeft') game.keys.left = false;
      if (e.key === 'ArrowRight') game.keys.right = false;
      if (e.key === ' ' || e.key === 'ArrowUp') game.keys.jump = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    const resetGame = () => {
      game.player.x = 30;
      game.player.y = 368;
      game.player.velocityY = 0;
      game.player.velocityX = 0;
      game.player.isJumping = false;
      game.itemsCollected = 0;
      game.items.forEach(item => item.collected = false);
      game.exit.active = false;
      gameStateRef.current = 'playing';
      airRef.current = 100;
      scoreRef.current = 0;
      setGameState('playing');
      setScore(0);
      setAir(100);
    };

    const checkCollision = (rect1, rect2) => {
      return rect1.x < rect2.x + rect2.width &&
             rect1.x + rect1.width > rect2.x &&
             rect1.y < rect2.y + rect2.height &&
             rect1.y + rect1.height > rect2.y;
    };

    const update = (deltaTime) => {
      if (gameStateRef.current !== 'playing') return;

      const player = game.player;
      const gravity = 0.6;
      const moveSpeed = 3;
      const jumpPower = -12;

      // Air decreases over time (every 2 seconds)
      airTimer += deltaTime;
      if (airTimer >= 2000) {
        airRef.current = Math.max(0, airRef.current - 1);
        setAir(airRef.current);

        if (airRef.current === 0) {
          gameStateRef.current = 'dead';
          setGameState('dead');
        }
        airTimer = 0;
      }

      // Player movement
      if (game.keys.left) {
        player.velocityX = -moveSpeed;
        player.direction = -1;
        if (player.onGround) {
          player.animFrame = (player.animFrame + 0.3) % 4;
        }
      } else if (game.keys.right) {
        player.velocityX = moveSpeed;
        player.direction = 1;
        if (player.onGround) {
          player.animFrame = (player.animFrame + 0.3) % 4;
        }
      } else {
        player.velocityX = 0;
        if (player.onGround) {
          player.animFrame = 0; // Standing still
        }
      }

      // Jump
      if (game.keys.jump && player.onGround && !player.isJumping) {
        player.velocityY = jumpPower;
        player.isJumping = true;
        player.onGround = false;
      }

      // Apply gravity
      player.velocityY += gravity;

      // Update position
      player.x += player.velocityX;
      player.y += player.velocityY;

      // Keep player in bounds
      if (player.x < 0) player.x = 0;
      if (player.x + player.width > 512) player.x = 512 - player.width;

      // Platform collision
      player.onGround = false;
      game.platforms.forEach(platform => {
        if (checkCollision(player, platform)) {
          // Landing on top
          if (player.velocityY > 0 && player.y + player.height - player.velocityY <= platform.y) {
            player.y = platform.y - player.height;
            player.velocityY = 0;
            player.onGround = true;
            player.isJumping = false;
          }
          // Hitting from below
          else if (player.velocityY < 0 && player.y - player.velocityY >= platform.y + platform.height) {
            player.y = platform.y + platform.height;
            player.velocityY = 0;
          }
          // Hitting from sides
          else if (player.velocityX > 0) {
            player.x = platform.x - player.width;
          } else if (player.velocityX < 0) {
            player.x = platform.x + platform.width;
          }
        }
      });

      // Enemy movement and collision
      game.enemies.forEach(enemy => {
        enemy.x += enemy.speed * enemy.direction;
        if (enemy.x <= enemy.minX || enemy.x >= enemy.maxX) {
          enemy.direction *= -1;
        }

        // Check player collision with enemy
        if (checkCollision(player, { x: enemy.x, y: enemy.y, width: 16, height: 16 })) {
          gameStateRef.current = 'dead';
          setGameState('dead');
        }
      });

      // Item collection
      game.items.forEach(item => {
        if (!item.collected) {
          if (checkCollision(player, { x: item.x, y: item.y, width: 8, height: 8 })) {
            item.collected = true;
            game.itemsCollected++;
            scoreRef.current += 100;
            setScore(scoreRef.current);

            // Activate exit when all items collected
            if (game.itemsCollected === game.totalItems) {
              game.exit.active = true;
            }
          }
        }
      });

      // Exit collision (only if active)
      if (game.exit.active && checkCollision(player, game.exit)) {
        gameStateRef.current = 'won';
        setGameState('won');
      }

      // Fall off screen
      if (player.y > 450) {
        gameStateRef.current = 'dead';
        setGameState('dead');
      }
    };

    const draw = () => {
      // Clear canvas with black background
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, 512, 416);

      // Draw platforms (white/cyan blocks with pattern)
      game.platforms.forEach(platform => {
        for (let x = platform.x; x < platform.x + platform.width; x += 8) {
          for (let y = platform.y; y < platform.y + platform.height; y += 8) {
            // Alternating brick pattern
            const isOdd = (Math.floor(x / 8) + Math.floor(y / 8)) % 2;
            ctx.fillStyle = isOdd ? '#00FFFF' : '#FFFFFF';
            ctx.fillRect(x, y, 8, 8);
            // Brick outline
            ctx.strokeStyle = '#888888';
            ctx.lineWidth = 1;
            ctx.strokeRect(x, y, 8, 8);
          }
        }
      });

      // Draw enemies (more authentic robot design)
      game.enemies.forEach(enemy => {
        const enemyX = Math.floor(enemy.x);
        const enemyY = Math.floor(enemy.y);

        // Robot body (magenta/red)
        ctx.fillStyle = '#FF00FF';
        ctx.fillRect(enemyX + 2, enemyY + 4, 12, 10);

        // Robot head
        ctx.fillStyle = '#FF0000';
        ctx.fillRect(enemyX + 4, enemyY, 8, 6);

        // Eyes (yellow)
        ctx.fillStyle = '#FFFF00';
        ctx.fillRect(enemyX + 5, enemyY + 2, 2, 2);
        ctx.fillRect(enemyX + 9, enemyY + 2, 2, 2);

        // Antenna
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(enemyX + 7, enemyY - 2, 2, 2);

        // Legs
        ctx.fillStyle = '#FF0000';
        ctx.fillRect(enemyX + 3, enemyY + 14, 3, 2);
        ctx.fillRect(enemyX + 10, enemyY + 14, 3, 2);
      });

      // Draw items (flashing keys - more authentic)
      const flashState = Math.floor(Date.now() / 150) % 2;
      game.items.forEach(item => {
        if (!item.collected) {
          ctx.fillStyle = flashState ? '#FFFF00' : '#00FFFF';
          // Key shape
          ctx.fillRect(item.x, item.y + 2, 6, 4);
          ctx.fillRect(item.x + 6, item.y + 3, 2, 2);
          ctx.fillRect(item.x - 2, item.y + 2, 2, 1);
          ctx.fillRect(item.x - 2, item.y + 5, 2, 1);
        }
      });

      // Draw exit (green portal - only visible when active)
      if (game.exit.active) {
        const flashExit = Math.floor(Date.now() / 100) % 3;
        const colors = ['#00FF00', '#00DD00', '#00BB00'];
        ctx.fillStyle = colors[flashExit];

        // Portal frame
        ctx.fillRect(game.exit.x, game.exit.y, game.exit.width, 4);
        ctx.fillRect(game.exit.x, game.exit.y + game.exit.height - 4, game.exit.width, 4);
        ctx.fillRect(game.exit.x, game.exit.y, 4, game.exit.height);
        ctx.fillRect(game.exit.x + game.exit.width - 4, game.exit.y, 4, game.exit.height);

        // Portal interior (animated)
        for (let i = 0; i < 3; i++) {
          ctx.fillStyle = colors[(flashExit + i) % 3];
          ctx.fillRect(game.exit.x + 6, game.exit.y + 6 + (i * 6), game.exit.width - 12, 4);
        }
      }

      // Draw player (Miner Willy - authentic animated sprite)
      const player = game.player;
      const px = Math.floor(player.x);
      const py = Math.floor(player.y);
      const animFrame = Math.floor(player.animFrame) % 4;
      const dir = player.direction;

      // Willy's ponytail/hair (white - behind head)
      ctx.fillStyle = '#FFFFFF';
      if (dir > 0) {
        ctx.fillRect(px + 1, py + 3, 2, 3);
      } else {
        ctx.fillRect(px + 13, py + 3, 2, 3);
      }

      // Head (bright cyan - mining helmet/head)
      ctx.fillStyle = '#00FFFF';
      ctx.fillRect(px + 4, py + 1, 8, 5);

      // Face (white/flesh)
      ctx.fillStyle = '#FFFFFF';
      if (dir > 0) {
        ctx.fillRect(px + 7, py + 3, 5, 3);
      } else {
        ctx.fillRect(px + 4, py + 3, 5, 3);
      }

      // Eye
      ctx.fillStyle = '#000000';
      if (dir > 0) {
        ctx.fillRect(px + 10, py + 3, 1, 1);
      } else {
        ctx.fillRect(px + 5, py + 3, 1, 1);
      }

      // Body (bright yellow/white)
      ctx.fillStyle = '#FFFF00';
      ctx.fillRect(px + 4, py + 6, 8, 5);

      // Arms
      ctx.fillStyle = '#FFFF00';
      if (animFrame === 1 || animFrame === 3) {
        // Arms out
        if (dir > 0) {
          ctx.fillRect(px + 12, py + 7, 2, 3);
          ctx.fillRect(px + 2, py + 8, 2, 2);
        } else {
          ctx.fillRect(px + 2, py + 7, 2, 3);
          ctx.fillRect(px + 12, py + 8, 2, 2);
        }
      } else {
        // Arms down
        ctx.fillRect(px + 2, py + 7, 2, 3);
        ctx.fillRect(px + 12, py + 7, 2, 3);
      }

      // Legs - animated walking
      ctx.fillStyle = '#FF00FF'; // Magenta legs

      if (player.onGround) {
        if (animFrame === 0) {
          // Both legs together
          ctx.fillRect(px + 6, py + 11, 2, 5);
          ctx.fillRect(px + 8, py + 11, 2, 5);
        } else if (animFrame === 1) {
          // Left leg forward
          ctx.fillRect(px + 5, py + 11, 2, 5);
          ctx.fillRect(px + 9, py + 11, 2, 5);
        } else if (animFrame === 2) {
          // Both legs together
          ctx.fillRect(px + 6, py + 11, 2, 5);
          ctx.fillRect(px + 8, py + 11, 2, 5);
        } else {
          // Right leg forward
          ctx.fillRect(px + 7, py + 11, 2, 5);
          ctx.fillRect(px + 9, py + 11, 2, 5);
        }
      } else {
        // Jumping - legs together stretched
        ctx.fillRect(px + 6, py + 11, 2, 5);
        ctx.fillRect(px + 8, py + 11, 2, 5);
      }

      // Boots (black)
      ctx.fillStyle = '#000000';
      if (player.onGround) {
        if (animFrame === 0 || animFrame === 2) {
          if (dir > 0) {
            ctx.fillRect(px + 6, py + 15, 3, 1);
            ctx.fillRect(px + 8, py + 15, 3, 1);
          } else {
            ctx.fillRect(px + 5, py + 15, 3, 1);
            ctx.fillRect(px + 7, py + 15, 3, 1);
          }
        } else if (animFrame === 1) {
          if (dir > 0) {
            ctx.fillRect(px + 5, py + 15, 3, 1);
            ctx.fillRect(px + 9, py + 15, 3, 1);
          } else {
            ctx.fillRect(px + 4, py + 15, 3, 1);
            ctx.fillRect(px + 8, py + 15, 3, 1);
          }
        } else {
          if (dir > 0) {
            ctx.fillRect(px + 7, py + 15, 3, 1);
            ctx.fillRect(px + 9, py + 15, 3, 1);
          } else {
            ctx.fillRect(px + 4, py + 15, 3, 1);
            ctx.fillRect(px + 6, py + 15, 3, 1);
          }
        }
      } else {
        // Jumping boots
        if (dir > 0) {
          ctx.fillRect(px + 6, py + 15, 3, 1);
          ctx.fillRect(px + 8, py + 15, 3, 1);
        } else {
          ctx.fillRect(px + 5, py + 15, 3, 1);
          ctx.fillRect(px + 7, py + 15, 3, 1);
        }
      }

      // Draw HUD (authentic Manic Miner style)
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, 512, 30);

      // Score
      ctx.fillStyle = '#FFFF00';
      ctx.font = 'bold 16px monospace';
      ctx.fillText(`High Score ${scoreRef.current.toString().padStart(6, '0')}`, 10, 20);

      // Air bar
      const airBarWidth = 100;
      const airFill = (airRef.current / 100) * airBarWidth;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillText('AIR', 200, 20);
      ctx.strokeStyle = '#FFFFFF';
      ctx.strokeRect(240, 8, airBarWidth, 12);

      // Air fill color changes based on amount
      if (airRef.current > 50) ctx.fillStyle = '#00FF00';
      else if (airRef.current > 25) ctx.fillStyle = '#FFFF00';
      else ctx.fillStyle = '#FF0000';
      ctx.fillRect(241, 9, airFill - 2, 10);

      // Items collected
      ctx.fillStyle = '#00FFFF';
      ctx.fillText(`Items ${game.itemsCollected}/${game.totalItems}`, 380, 20);

      // Game over messages
      if (gameStateRef.current === 'won') {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.fillRect(0, 150, 512, 120);

        // Flashing text
        const flash = Math.floor(Date.now() / 300) % 2;
        ctx.fillStyle = flash ? '#00FF00' : '#FFFF00';
        ctx.font = 'bold 40px monospace';
        ctx.fillText('CAVERN COMPLETE!', 60, 200);

        ctx.fillStyle = '#FFFFFF';
        ctx.font = '16px monospace';
        ctx.fillText('Press R to Play Again', 150, 240);
      } else if (gameStateRef.current === 'dead') {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.fillRect(0, 150, 512, 120);

        const flash = Math.floor(Date.now() / 300) % 2;
        ctx.fillStyle = flash ? '#FF0000' : '#FF00FF';
        ctx.font = 'bold 40px monospace';
        ctx.fillText('GAME OVER', 130, 200);

        ctx.fillStyle = '#FFFFFF';
        ctx.font = '16px monospace';
        ctx.fillText('Press R to Try Again', 150, 240);
      }
    };

    const gameLoop = (timestamp) => {
      const deltaTime = timestamp - lastTime;
      lastTime = timestamp;

      update(deltaTime);
      draw();

      animationFrameId = requestAnimationFrame(gameLoop);
    };

    animationFrameId = requestAnimationFrame(gameLoop);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []); // Empty dependencies - only run once

  return (
    <div className="manic-miner-container">
      <div className="game-header">
        <h1 className="game-title">MANIC MINER</h1>
        <h2 className="level-title">Level 1: Central Cavern</h2>
      </div>
      <div className="canvas-wrapper">
        <canvas
          ref={canvasRef}
          width={512}
          height={416}
          className="game-canvas"
        />
      </div>
      <div className="game-instructions">
        <p><strong>Controls:</strong> Arrow Keys to move, SPACE or UP to jump</p>
        <p><strong>Objective:</strong> Collect all 8 items to open the exit portal</p>
        <p><strong>Watch out:</strong> Avoid the red robots and don't run out of air!</p>
      </div>
    </div>
  );
};

export default ManicMiner;
